import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, ArrowRight, AlertOctagon, Users2, FolderKanban, UserX } from "lucide-react";
import { StandupCard, shiftDay, prettyDay } from "../components/project/StandupPanel";
import { TeamStreaks, useStandupStats } from "../components/project/StandupStreaks";

export default function StandupOverview() {
  const [data, setData] = useState(null);
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  const stats = useStandupStats();

  const load = async (d) => {
    try { const { data } = await api.get("/standups/overview", { params: d ? { date: d } : {} }); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-8 text-sm text-neutral-500">Loading standups…</div>;

  const isToday = data.date === data.today;
  const list = onlyBlocked ? data.projects.filter(p => p.blockers > 0) : data.projects;
  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]" data-testid="standup-overview">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Development</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Standups</h1>
          <p className="text-neutral-600 text-sm mt-1" data-testid="so-date">{isToday ? "Today" : prettyDay(data.date)} across all your active projects</p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={() => load(shiftDay(data.date, -1))} data-testid="so-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => load()} disabled={isToday} data-testid="so-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => load(shiftDay(data.date, 1))} disabled={isToday} data-testid="so-next-day"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      <Totals t={data.totals} onlyBlocked={onlyBlocked} setOnlyBlocked={setOnlyBlocked} />
      <TeamStreaks stats={stats} />
      {list.length === 0 && <div className="panel p-8 text-center text-sm text-neutral-500" data-testid="so-empty">{onlyBlocked ? "Nobody is blocked. Nice." : "No active projects to show."}</div>}
      <div className="space-y-4">
        {list.map(p => <ProjectStandups key={p.project_id} p={p} isToday={isToday} />)}
      </div>
    </div>
  );
}

function Totals({ t, onlyBlocked, setOnlyBlocked }) {
  const card = (Icon, label, n, cls, testid) => (
    <div className="panel p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-neutral-500"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className={`text-3xl font-bold mt-1 ${cls}`}>{n}</div>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {card(FolderKanban, "Projects", t.projects, "text-neutral-900", "so-total-projects")}
        {card(Users2, "Notes posted", t.posted, "text-blue-700", "so-total-posted")}
        {card(AlertOctagon, "Blocked", t.blockers, t.blockers ? "text-rose-600" : "text-emerald-600", "so-total-blockers")}
        {card(UserX, "Not posted", t.missing, "text-amber-600", "so-total-missing")}
      </div>
      <button onClick={() => setOnlyBlocked(v => !v)} data-testid="so-only-blocked"
        className={`text-[11px] font-mono uppercase tracking-wider px-3 py-1 rounded-full border transition-colors ${onlyBlocked ? "bg-rose-100 text-rose-700 border-rose-300" : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300"}`}>
        Only blocked projects
      </button>
    </div>
  );
}

function ProjectStandups({ p, isToday }) {
  return (
    <div className={`panel p-4 space-y-3 ${p.blockers ? "border-rose-300" : ""}`} data-testid={`so-project-${p.project_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-bold">{p.project_name}</h3>
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${p.blockers ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-emerald-50 border-emerald-300 text-emerald-700"}`}>
            {p.blockers ? `${p.blockers} blocked` : "No blockers"}
          </span>
          <span className="text-[10px] font-mono text-neutral-500">{p.notes.length} posted</span>
        </div>
        <Link to={`/projects/${p.project_id}?tab=standup`} className="text-xs text-orange-600 hover:underline flex items-center gap-1" data-testid={`so-open-${p.project_id}`}>
          Open project <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      {p.missing.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-neutral-500 font-mono">{isToday ? "Not posted yet:" : "Didn't post:"}</span>
          {p.missing.map(u => <span key={u.id} className="px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200 text-neutral-600">{u.name}</span>)}
        </div>
      )}
      {p.notes.length > 0 ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{p.notes.map(n => <StandupCard key={n.id} n={n} />)}</div>
      ) : <div className="text-xs text-neutral-500">No notes yet.</div>}
    </div>
  );
}
